import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  startOfMonth,
  startOfWeek,
  startOfDay,
  endOfDay,
  format,
} from "date-fns";
import type { Task } from "../types";
import { isInstallation } from "./installation";

export type CalendarScale = "year" | "month" | "day";
/** Keep a montage on the same track when its range crosses a week boundary. */
export function installationTracks(tasks: Task[]) {
  const ends: string[] = [];
  return [...tasks]
    .filter((t) => t.installation_start && t.installation_end)
    .sort(
      (a, b) =>
        a.installation_start!.localeCompare(b.installation_start!) ||
        (a.scheduled_start && b.scheduled_start
          ? new Date(a.scheduled_start).getTime() -
            new Date(b.scheduled_start).getTime()
          : 0) ||
        String(a.id).localeCompare(String(b.id)),
    )
    .map((task) => {
      let track = ends.findIndex((end) => end < task.installation_start!);
      if (track < 0) track = ends.length;
      ends[track] = task.installation_end!;
      return { task, track };
    });
}
export function monthWeeks(date: Date) {
  const days = eachDayOfInterval({
    start: startOfWeek(startOfMonth(date), { weekStartsOn: 1 }),
    end: endOfWeek(endOfMonth(date), { weekStartsOn: 1 }),
  });
  return Array.from({ length: days.length / 7 }, (_, i) =>
    days.slice(i * 7, i * 7 + 7),
  );
}
export function tasksOnDay(tasks: Task[], day: Date) {
  const first = startOfDay(day).getTime(),
    last = endOfDay(day).getTime();
  return tasks
    .filter(
      (task) =>
        !isInstallation(task) &&
        !!task.scheduled_start &&
        new Date(task.scheduled_start).getTime() <= last &&
        new Date(task.scheduled_end || task.scheduled_start).getTime() > first,
    )
    .sort(
      (a, b) =>
        new Date(a.scheduled_start!).getTime() -
          new Date(b.scheduled_start!).getTime() ||
        String(a.id).localeCompare(String(b.id)),
    );
}
/** Index once per visible range instead of scanning history for every day cell. */
export function taskDaysMap(tasks: Task[], first: Date, last: Date) {
  const result = new Map<string, Task[]>();
  const min = startOfDay(first).getTime(),
    max = endOfDay(last).getTime();
  const ordered = [...tasks].sort(
    (a, b) =>
      new Date(a.scheduled_start!).getTime() -
        new Date(b.scheduled_start!).getTime() ||
      String(a.id).localeCompare(String(b.id)),
  );
  for (const task of ordered) {
    if (isInstallation(task) || !task.scheduled_start) continue;
    const start = Math.max(min, new Date(task.scheduled_start).getTime()),
      end = Math.min(
        max,
        new Date(task.scheduled_end || task.scheduled_start).getTime() - 1,
      );
    if (!Number.isFinite(start) || !Number.isFinite(end) || end < start)
      continue;
    for (const day of eachDayOfInterval({
      start: new Date(start),
      end: new Date(end),
    })) {
      const key = format(day, "yyyy-MM-dd"),
        entries = result.get(key) || [];
      entries.push(task);
      result.set(key, entries);
    }
  }
  return result;
}
export const taskCompleted = (task: Task) =>
  task.status === "completed" || !!task.done_date;
export const personalTask = (
  task: Pick<Task, "team_task" | "sales_id" | "created_by_sales_id">,
  employee?: string | number,
) =>
  !task.team_task &&
  task.sales_id != null &&
  String(task.sales_id) === String(employee) &&
  String(task.created_by_sales_id) === String(employee);
